import type { IsoDate, IsoTimestamp, SettingDateFormat } from "@/types"

const relativeTime = new Intl.RelativeTimeFormat("en", { numeric: "always" })

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 24 * 60 * 60],
  ["month", 30 * 24 * 60 * 60],
  ["day", 24 * 60 * 60],
  ["hour", 60 * 60],
  ["minute", 60],
]

/** "5 minutes ago" for an ISO timestamp; "just now" under a minute. */
export function timeAgo(isoTimestamp: IsoTimestamp, now: Date = new Date()): string {
  const seconds = Math.round((now.getTime() - new Date(isoTimestamp).getTime()) / 1000)

  for (const [unit, unitSeconds] of UNITS) {
    if (seconds >= unitSeconds) return relativeTime.format(-Math.floor(seconds / unitSeconds), unit)
  }

  return "just now"
}

/** "1 transaction", "2 transactions". */
export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`
}

function dateTimeParts(isoTimestamp: IsoTimestamp, timeZone?: string): Record<string, string> {
  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  })

  return Object.fromEntries(formatter.formatToParts(new Date(isoTimestamp)).map((part) => [part.type, part.value]))
}

type DateParts = { year: string; month: string; day: string }

/** The date formats an account can choose; the keys match RegionalFormat::DATE_FORMATS in Rails. */
const DATE_FORMATS: Record<SettingDateFormat, (parts: DateParts) => string> = {
  iso: ({ year, month, day }) => `${year}-${month}-${day}`,
  dmy_dot: ({ year, month, day }) => `${day}.${month}.${year}`,
  dmy_slash: ({ year, month, day }) => `${day}/${month}/${year}`,
  dmy_dash: ({ year, month, day }) => `${day}-${month}-${year}`,
  mdy_slash: ({ year, month, day }) => `${month}/${day}/${year}`,
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

/** True for an ISO date such as "2026-10-31". Narrows a string no serializer could type, such as a cell of an uploaded statement. */
export function isIsoDate(value: string): value is IsoDate {
  return ISO_DATE.test(value)
}

/** The string as an IsoDate, for a value that is a date only by context (a correction whose type is "date"); throws for anything else. */
export function isoDate(value: string): IsoDate {
  if (!isIsoDate(value)) throw new Error(`Not an ISO date: ${value}`)
  return value
}

/** "31.10.2026" for the ISO date "2026-10-31" in the `dmy_dot` format. */
export function formatDate(isoDate: IsoDate, dateFormat: SettingDateFormat): string {
  const match = ISO_DATE.exec(isoDate)
  if (!match) throw new Error(`Not an ISO date: ${isoDate}`)

  const [year, month, day] = match.slice(1) as [string, string, string]
  return DATE_FORMATS[dateFormat]({ year, month, day })
}

/** "28.10.2026" in the `dmy_dot` format for the day of the timestamp "2026-10-28T12:57:25Z" in the viewer's time zone. Pass `timeZone` to pin it. */
export function formatTimestampDate(isoTimestamp: IsoTimestamp, dateFormat: SettingDateFormat, timeZone?: string): string {
  // A plain date would be read as midnight UTC and shift to the day before west of UTC: formatDate is for those.
  if (ISO_DATE.test(isoTimestamp)) throw new Error(`Not a timestamp: ${isoTimestamp}`)

  return DATE_FORMATS[dateFormat](dateTimeParts(isoTimestamp, timeZone) as DateParts)
}

/** "2026-10-12 09:07" in the viewer's time zone, with the date in `dateFormat`. Pass `timeZone` to pin it. */
export function formatDateTime(isoTimestamp: IsoTimestamp, dateFormat: SettingDateFormat, timeZone?: string): string {
  const parts = dateTimeParts(isoTimestamp, timeZone)
  return `${DATE_FORMATS[dateFormat](parts as DateParts)} ${parts.hour}:${parts.minute}`
}

/** "ingestion_failed" becomes "Ingestion failed". */
export function humanize(value: string): string {
  const words = value.replaceAll("_", " ")
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** "board_notes" becomes "Board Notes". */
export function titleize(value: string): string {
  return value
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}

/**
 * Today in the viewer's time zone as an ISO date, such as "2026-11-02". Built from the local date
 * parts: `toISOString()` is UTC and gives yesterday east of UTC in the hours after midnight.
 * A plain string, since it seeds date inputs the person edits.
 */
export function todayISO(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, "0")
  const day = String(now.getDate()).padStart(2, "0")
  return `${now.getFullYear()}-${month}-${day}`
}

/** The ISO date `days` after `isoDate`: `addDays("2026-01-31", 1)` is "2026-02-01". */
export function addDays(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T00:00:00Z`)
  if (Number.isNaN(date.getTime())) throw new Error(`Not an ISO date: ${isoDate}`)

  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}

const DAY_MONTH = {
  short: new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", timeZone: "UTC" }),
  long: new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "long", timeZone: "UTC" }),
}

/** "02 Oct" for the ISO date "2026-10-02"; "02 October" with `month: "long"`. */
export function formatDayMonth(isoDate: IsoDate, month: keyof typeof DAY_MONTH = "short"): string {
  const date = new Date(`${isoDate}T00:00:00Z`)
  if (Number.isNaN(date.getTime())) throw new Error(`Not an ISO date: ${isoDate}`)

  return DAY_MONTH[month].format(date)
}

const MONTH_YEAR = new Intl.DateTimeFormat("en-GB", { month: "short", year: "numeric", timeZone: "UTC" })

/** "Sep 2026" for the month key "2026-09" (Month.key_for). */
export function formatMonth(monthKey: string): string {
  const date = new Date(`${monthKey}-01T00:00:00Z`)
  if (!/^\d{4}-\d{2}$/.test(monthKey) || Number.isNaN(date.getTime())) throw new Error(`Not a month key: ${monthKey}`)

  return MONTH_YEAR.format(date)
}

/** The browser's IANA time zone, such as "Europe/Nicosia"; empty when the browser does not say. */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone ?? ""
}
